import React from 'react';

import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import type { WRelatedLinkView } from '@zola/svc-web-api-ts-client';

import { FAQItems } from 'constants/websitev3/faqs';
import { gridItems } from 'constants/websitev3/gridItems';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import { reviewItems } from 'constants/websitev3/reviews';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';

import ArticleLogos from 'components/WebsiteLp/components/ArticleLogos';
import GridTwoByTwo from '@zola/zola-ui/src/components/PreAuthV3/GridTwoByTwo';
import Header from 'components/LP/WeddingPlanning/website/Header';
import Nav from 'components/Nav';
import NextBackgroundForegroundImages from 'components/PreauthV3/NextBackgroundForegroundImages';
import Masthead from '@zola/zola-ui/src/components/PreAuthV3/Masthead';
import SupportAccordion from '@zola/zola-ui/src/components/PreAuthV3/SupportAccordion';
import T from '@zola/zola-ui/src/typography/Text';
import Testimonials from 'components/WebsiteLp/components/Testimonials';
import ThemeSelector from 'components/WebsiteLp/components/ThemeSelector';

import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import { PageWrapper } from 'components/LP/v3/PageWrapper.styles';
import Footer from 'components/ui/Footer/Footer';
import { StyledHTitle3 } from './WebsiteLp.styles';

const HeroDesktop = '/static-preauth/images/WeddingWebsiteLP/V3/Masthead/HeroDesktop.png';

export type WebsiteLpProps = {
  navHTML: string | null;
  userCtx: UserContext | null;
  adviceLinks: WRelatedLinkView[];
};

const WebsiteLP: React.FC<WebsiteLpProps> = (props): JSX.Element => {
  const { navHTML, userCtx, adviceLinks } = props;
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);
  const imgAltText = 'Build your FREE website, 600+ customizable options';

  return (
    <>
      <Header />
      <Nav
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disablePrimaryNavCollapse
        disableSecondary
        {...(isPostAuthNav3Enabled && { activePrimaryLink: PrimaryNavIdentifier.WEBSITE })}
      />
      <PageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <section>
          <Masthead
            btnHref="/wedding/onboard/wedding-website"
            btnText="Start your free website"
            secondaryBtnHref="/search/wedding-registry"
            secondaryBtnText="Find a couple"
            secondaryBtnAsLink
            isPhotoOnRight
            altMedia={
              <NextBackgroundForegroundImages
                bgColor={COLORS3.WHITE_100}
                foregroundImg={{
                  src: HeroDesktop,
                  alt: imgAltText,
                }}
                priority
              />
            }
            title={
              <h1>
                The best <span>wedding websites</span>: fast, <span>free</span> and foolproof
              </h1>
            }
            valueProps={[
              'Customizable template designs for every event, made to match your invites',
              'Digitally manage RSVPs for all your guests',
              'Home to your wedding registry',
            ]}
          />
        </section>
        <ThemeSelector />
        <GridTwoByTwo
          gridItems={gridItems}
          title={
            <StyledHTitle3 presentation="h2">
              Tools that are <T.Serif>stress-free,</T.Serif> hassle-free, cost-free
            </StyledHTitle3>
          }
        />
        <Testimonials />
        <ArticleLogos />
        <section>
          <SupportAccordion
            advice={{ links: adviceLinks }}
            faqs={FAQItems}
            reviews={reviewItems}
            collapsed
          />
        </section>
        <Footer userCtx={userCtx} />
      </PageWrapper>
    </>
  );
};

export default WebsiteLP;
